"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { buildWorkspaceCrumbsFromPath } from "@/components/ui-system/workspace-breadcrumb";
import { listTenants } from "@/lib/api/endpoints/tenants";
import { env } from "@/lib/env";
import { cn } from "@/lib/utils/cn";
import { useAuthStore } from "@/modules/auth/store";
import { useWorkspaceChromeStore } from "@/store/workspace-chrome";

export function WorkspaceStickyTitle({ pathname }: { pathname: string }) {
  const user = useAuthStore((s) => s.user);
  const tenantId = useAuthStore((s) => s.tenantId);
  const title = useWorkspaceChromeStore((s) => s.title);
  const titleHref = useWorkspaceChromeStore((s) => s.titleHref);
  const titleAdornment = useWorkspaceChromeStore((s) => s.titleAdornment);
  const showSubtitle = useWorkspaceChromeStore((s) => s.showSubtitle);
  const tenantsQuery = useQuery({
    queryKey: ["tenant-memberships", user?.id ?? ""],
    queryFn: listTenants,
    enabled: Boolean(user),
    staleTime: 60_000,
  });

  const pathCrumbs = useMemo(() => buildWorkspaceCrumbsFromPath(pathname), [pathname]);
  const resolvedTitle =
    title.trim() !== "" ? title : pathCrumbs[pathCrumbs.length - 1]?.label ?? "Workspace";
  const activeWorkspace = (tenantsQuery.data ?? []).find((tenant) => tenant.id === tenantId);
  const logoUrl = activeWorkspace?.logo_url
    ? `${env.publicApiUrl}${activeWorkspace.logo_url}`
    : null;

  return (
    <div className="flex min-w-0 flex-1 items-center gap-ds-3">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          className="h-12 w-12 shrink-0 rounded-control border border-card-border bg-surface-primary object-contain p-1"
        />
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-ds-2">
          {titleHref ? (
            <Link
              to={titleHref}
              title={resolvedTitle}
              className="min-w-0 max-w-full truncate font-sans text-lg font-semibold tracking-tight text-foreground underline-offset-4 transition-colors hover:text-link hover:underline md:text-xl"
            >
              {resolvedTitle}
            </Link>
          ) : (
            <h1
              title={resolvedTitle}
              className={cn(
                "min-w-0 max-w-full truncate font-sans text-lg font-semibold tracking-tight text-foreground md:text-xl",
              )}
            >
              {resolvedTitle}
            </h1>
          )}
          {titleAdornment}
        </div>
        {showSubtitle ? <p className="hidden text-micro text-muted sm:block">Industrial telemetry and energy monitoring</p> : null}
      </div>
    </div>
  );
}
